export type Difficulty = 'easy' | 'medium' | 'hard'
export type MoneyMode  = 'identify' | 'count' | 'pay'

export interface MoneyDenomination {
  id:         number
  imageUrl:   string
  wordEs:     string
  wordEn:     string
  valueCents: number
}

export interface McqOption {
  label:      string
  valueCents: number
  isCorrect:  boolean
}

export interface IdentifyRound {
  type:    'identify'
  denom:   MoneyDenomination
  options: McqOption[]
}

export interface CountRound {
  type:       'count'
  items:      MoneyDenomination[]   // may include repeated denominations
  totalCents: number
  options:    McqOption[]
}

export interface PayRound {
  type:        'pay'
  targetCents: number
}

export type Round = IdentifyRound | CountRound | PayRound

// ── Formatting ────────────────────────────────────────────────

// 245 → "2,45 €"  |  100 → "1,00 €"  |  5 → "0,05 €"
export function formatCents(cents: number): string {
  const euros    = Math.floor(cents / 100)
  const centsPart = cents % 100
  return `${euros},${centsPart.toString().padStart(2, '0')} €`
}

// ── Difficulty configs ────────────────────────────────────────

export const IDENTIFY_CONFIG: Record<Difficulty, { rounds: number; optionCount: number }> = {
  easy:   { rounds: 5,  optionCount: 3 },
  medium: { rounds: 8,  optionCount: 4 },
  hard:   { rounds: 12, optionCount: 4 },
}

export const COUNT_CONFIG: Record<Difficulty, {
  rounds:   number
  minItems: number
  maxItems: number
  maxCents: number
}> = {
  easy:   { rounds: 5,  minItems: 2, maxItems: 3, maxCents: 100  },
  medium: { rounds: 7,  minItems: 3, maxItems: 4, maxCents: 500  },
  hard:   { rounds: 10, minItems: 4, maxItems: 5, maxCents: 2000 },
}

export const PAY_CONFIG: Record<Difficulty, {
  rounds:        number
  maxCents:      number
  maxValueCents: number   // highest denomination allowed in wallet
}> = {
  easy:   { rounds: 5,  maxCents: 100,  maxValueCents: 100  },   // coins up to €1
  medium: { rounds: 7,  maxCents: 500,  maxValueCents: 500  },   // coins + €5
  hard:   { rounds: 10, maxCents: 2000, maxValueCents: 2000 },   // all up to €20
}

// ── Helpers ───────────────────────────────────────────────────

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function localShuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// Build distractors for identify mode using real denomination names.
// Deduplicates by valueCents and sorts by proximity so options feel plausible.
function buildIdentifyDistractors(
  correctValueCents: number,
  allDenoms:         MoneyDenomination[],
  count:             number,
  language:          string,
): McqOption[] {
  const seen = new Set<number>([correctValueCents])
  const candidates: MoneyDenomination[] = []
  for (const d of allDenoms) {
    if (!seen.has(d.valueCents)) {
      seen.add(d.valueCents)
      candidates.push(d)
    }
  }
  candidates.sort((a, b) =>
    Math.abs(a.valueCents - correctValueCents) - Math.abs(b.valueCents - correctValueCents),
  )
  return candidates.slice(0, count).map(d => ({
    label:      language === 'en' ? d.wordEn : d.wordEs,
    valueCents: d.valueCents,
    isCorrect:  false,
  }))
}

// Generate 3 plausible wrong values for MCQ options (count mode).
// Strategy: nearby denomination values first, then ±offset if needed.
function buildDistractorValues(
  correctCents: number,
  allValues:    number[],   // unique denomination values from theme
  count:        number,
): number[] {
  const others = allValues.filter(v => v !== correctCents)
  // Sort by proximity to the correct value so distractors feel plausible
  others.sort((a, b) => Math.abs(a - correctCents) - Math.abs(b - correctCents))

  const result: number[] = []
  for (const v of others) {
    if (result.length >= count) break
    result.push(v)
  }

  // If not enough real denominations, pad with ±coin offsets
  const offsets = [5, 10, 20, 50, 100, 2, 1]
  for (const offset of offsets) {
    if (result.length >= count) break
    const candidate = correctCents + offset
    if (candidate > 0 && !result.includes(candidate) && candidate !== correctCents) {
      result.push(candidate)
    }
  }
  for (const offset of offsets) {
    if (result.length >= count) break
    const candidate = correctCents - offset
    if (candidate > 0 && !result.includes(candidate) && candidate !== correctCents) {
      result.push(candidate)
    }
  }

  return result.slice(0, count)
}

// ── Round generators ──────────────────────────────────────────

export function generateIdentifyRound(
  denoms:     MoneyDenomination[],
  difficulty: Difficulty,
  language:   string,
): IdentifyRound {
  const { optionCount } = IDENTIFY_CONFIG[difficulty]
  const correct      = randomItem(denoms)
  const distractors  = buildIdentifyDistractors(correct.valueCents, denoms, optionCount - 1, language)

  const options = localShuffle([
    {
      label:      language === 'en' ? correct.wordEn : correct.wordEs,
      valueCents: correct.valueCents,
      isCorrect:  true,
    },
    ...distractors,
  ])

  return { type: 'identify', denom: correct, options }
}

export function generateCountRound(
  denoms:     MoneyDenomination[],
  difficulty: Difficulty,
): CountRound {
  const { minItems, maxItems, maxCents } = COUNT_CONFIG[difficulty]
  const eligible = denoms.filter(d => d.valueCents <= maxCents)
  const pool     = eligible.length > 0 ? eligible : denoms

  const count = minItems + Math.floor(Math.random() * (maxItems - minItems + 1))
  const items: MoneyDenomination[] = []
  let total = 0

  for (let i = 0; i < count; i++) {
    const remaining = maxCents - total
    const fit = pool.filter(d => d.valueCents <= remaining)
    if (fit.length === 0) break
    const picked = randomItem(fit)
    items.push(picked)
    total += picked.valueCents
  }

  if (items.length === 0) {
    const fallback = randomItem(pool)
    items.push(fallback)
    total = fallback.valueCents
  }

  const totalCents = items.reduce((s, d) => s + d.valueCents, 0)
  const allValues  = [...new Set(denoms.map(d => d.valueCents))]
  const distractors = buildDistractorValues(totalCents, allValues, 3)

  const options = localShuffle([
    { label: formatCents(totalCents), valueCents: totalCents, isCorrect: true },
    ...distractors.map(v => ({ label: formatCents(v), valueCents: v, isCorrect: false })),
  ])

  return { type: 'count', items, totalCents, options }
}

export function generatePayRound(
  denoms:     MoneyDenomination[],
  difficulty: Difficulty,
): PayRound {
  const { maxCents, maxValueCents } = PAY_CONFIG[difficulty]
  const eligible = denoms.filter(d => d.valueCents <= maxValueCents)
  const pool     = eligible.length > 0 ? eligible : denoms

  // Build a target price by summing 2–4 random eligible denominations
  const componentsCount = 2 + Math.floor(Math.random() * 3)
  let targetCents = 0

  for (let i = 0; i < componentsCount; i++) {
    const remaining = maxCents - targetCents
    if (remaining <= 0) break
    const fit = pool.filter(d => d.valueCents <= remaining)
    if (fit.length === 0) break
    targetCents += randomItem(fit).valueCents
  }

  if (targetCents === 0) targetCents = randomItem(pool).valueCents

  return { type: 'pay', targetCents }
}

export function generateRounds(
  denoms:     MoneyDenomination[],
  mode:       MoneyMode,
  difficulty: Difficulty,
  language:   string,
): Round[] {
  if (denoms.length === 0) return []

  const roundCount =
    mode === 'identify' ? IDENTIFY_CONFIG[difficulty].rounds
    : mode === 'count'  ? COUNT_CONFIG[difficulty].rounds
    : PAY_CONFIG[difficulty].rounds

  return Array.from({ length: roundCount }, () => {
    if (mode === 'identify') return generateIdentifyRound(denoms, difficulty, language)
    if (mode === 'count')    return generateCountRound(denoms, difficulty)
    return generatePayRound(denoms, difficulty)
  })
}

export function getWalletDenoms(
  denoms:     MoneyDenomination[],
  difficulty: Difficulty,
): MoneyDenomination[] {
  const { maxValueCents } = PAY_CONFIG[difficulty]
  const eligible = denoms.filter(d => d.valueCents <= maxValueCents)
  // Deduplicate by valueCents, keeping first occurrence
  const seen = new Set<number>()
  return eligible.filter(d => {
    if (seen.has(d.valueCents)) return false
    seen.add(d.valueCents)
    return true
  })
}
